import {
    useRef,
    useState
} from 'react';

import {SimpleGrid} from '@mantine/core';

import {usePaymentProfiles} from '../hooks/usePaymentProfiles.ts';
import {usePaymentProfilesSelection} from '../hooks/usePaymentProfilesSelection.ts';
import {useTableColumns} from '../hooks/useTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {update} from '../services/PaymentProfilesService.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableTokenPagination} from "../../../components/DataTableTokenPagination.tsx";
import {PaymentProfile} from '../types';
import {useQueryClient} from '@tanstack/react-query';

export const RecordsTable = ({customerId, billingProviderId}: { customerId?: string; billingProviderId?: string }) => {
    const {selectedRecords, setSelectedRecords} = usePaymentProfilesSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<PaymentProfile | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = usePaymentProfiles({customerId, billingProviderId});

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleRecordClick = (record: PaymentProfile) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: PaymentProfile) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, values);
            queryClient.invalidateQueries({queryKey: ['payment-profiles']});
        }
    };

    const {columns} = useTableColumns({onRecordClick: handleRecordClick, billingProviderId});

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken}
                                      handleLoadMore={fetchRecords}/>

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
